import React, { Fragment, useContext, useState } from "react";
import {
  firstError,
  hasError,
  validateContent,
  validateContentField,
} from "../contentValidation";
import { MediaContext } from "../../common/media-context";
import {
  useDocumentMediumFormats,
  useImageMediumFormats,
} from "../../vocabularyServices";
import {
  always,
  anyPass,
  find,
  ifElse,
  includes,
  isEmpty,
  lensProp,
  pipe,
  prop,
  set,
  view,
} from "ramda";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import {
  useIsAdmin,
  useIsIapReviewer,
  useIsUserIAPManager,
} from "../../../auth/authUtils";
import { isAlbum, isAudiobook, isChapterBook } from "../../../util/mediaUtil";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import AllowDownloadCheckbox from "./AllowDownloadCheckbox";
import AudioTypeSelect from "./AudioTypeSelect";
import CopyIcon from "../../common/CopyIcon";
import EditChaptersDialog from "./EditChaptersDialog";
import EditColumn from "../../common/EditColumn";
import LanguageSelect from "./LanguageSelect";
import LicenseTypeSelect from "./LicenseTypeSelect";
import MediumFormatSelect from "./MediumFormatSelect";
import OrganizationAutoComplete from "../../../user-admin/OrganizationAutoComplete";
import PublicationDate from "../../common/PublicationDate";

const validator = {
  validateField: validateContentField,
  validate: validateContent,
};

const getLocalLibrary = pipe(
  find(prop("localLibrary")),
  ifElse(isBlank, always(""), view(lensProp("localLibrary"))),
);

const EditContentInfoColumn = props => {
  const { className } = props;
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();
  const isIAPManager = useIsUserIAPManager();
  const isIapReviewer = useIsIapReviewer();

  const documentMediumFormats = useDocumentMediumFormats();
  const imageMediumFormats = useImageMediumFormats();

  //-- Manage Edit Chapters Dialog
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleCancel = () => setOpen(false);
  const handleClose = () => {
    setOpen(false);
  };

  const isRequired = field => {
    const { type } = context.media;
    return (
      notBlank(context.requiredFields[type]) &&
      includes(field, context.requiredFields[type])
    );
  };

  const handleBlur = field => event => {
    const value = context.media[field];

    context.setErrors(prev =>
      context.validator.validateField(context.media.type, field, value, prev),
    );
  };

  const formBuilder = handlers => {
    return (
      <Fragment>
        {anyPass([isAlbum, isAudiobook, isChapterBook])(context.media) &&
          !isIapReviewer && (
            <Grid item container justify="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6">
                  {isAlbum(context.media) ? "Tracks" : "Chapters"}
                </Typography>
              </Grid>

              <Grid item>
                <EditChaptersDialog
                  open={open}
                  onCancel={handleCancel}
                  onClose={handleClose}
                />

                <Button color="primary" variant="outlined" onClick={handleOpen}>
                  Manage
                </Button>
              </Grid>
            </Grid>
          )}

        {anyTrue([isAdmin, isIAPManager]) && (
          <Grid item container spacing={2}>
            <Grid item md={6} xs={12}>
              <TextField
                id="content-type"
                fullWidth
                label="Content Type"
                value={context.media.type}
                disabled
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                id="content-mime-type"
                fullWidth
                label="Mimetype"
                value={context.media.mimeType}
                disabled
              />
            </Grid>
          </Grid>
        )}

        {notBlank(context.media.id) && (
          <Grid item>
            <TextField
              id="content-id"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyIcon value={context.media.id} text="Content ID" />
                  </InputAdornment>
                ),
              }}
              label="Content ID"
              value={context.media.id}
              disabled
            />
          </Grid>
        )}

        {context.media.type === "IMAGE" && (
          <Grid item>
            <TextField
              id="image-alt-text"
              fullWidth
              required
              inputProps={{
                maxLength: 150,
              }}
              label="Alt Text"
              value={context.media.altText}
              onChange={handlers.onTextChange(["altText"])}
              error={hasError(context.errors, "altText")}
              helperText={firstError(context.errors, "altText")}
              onBlur={handleBlur("altText")}
            />
          </Grid>
        )}

        <Grid item>
          <LanguageSelect
            required={isRequired("languageIso639_2")}
            value={context.media.languageIso639_2}
            onChange={handlers.onAutocompleteChange("id", ["languageIso639_2"])}
          />
        </Grid>

        {context.isUpload && context.media.type === "ARTICLE" && (
          <Grid item>
            <TextField
              id={`content-upload-periodical`}
              fullWidth
              required={isRequired("periodical")}
              label="Periodical"
              value={context.media.periodical}
              onChange={handlers.onTextChange(["periodical"])}
            />
          </Grid>
        )}

        {(context.media.type === "OTHER_DOCUMENT" ||
          context.media.type === "IMAGE") && (
          <Grid item>
            <MediumFormatSelect
              required={isRequired("mediumFormatCode")}
              value={context.media.mediumFormatCode}
              onChange={handlers.onAutocompleteChange("name", [
                "mediumFormatCode",
              ])}
              mediumFormats={
                context.media.type === "OTHER_DOCUMENT"
                  ? documentMediumFormats
                  : imageMediumFormats
              }
            />
          </Grid>
        )}

        {context.media.type === "AUDIO" && (
          <Grid item>
            <AudioTypeSelect
              required={isRequired("audioType")}
              value={context.media.audioType}
              onChange={handlers.onTextChange(["audioType"])}
            />
          </Grid>
        )}

        <Grid item container spacing={2} style={{ paddingRight: 0 }}>
          <PublicationDate
            errors={context.errors}
            isRequired={isRequired}
            handleBlur={handleBlur}
            media={context.media}
            onCheckboxChange={handlers.onCheckboxChange}
            onDateChange={handlers.onDateChange}
            onTextChange={handlers.onTextChange}
            validator={context.validator}
          />
        </Grid>

        <Grid container item direction="column">
          <LicenseTypeSelect
            required={isRequired("licenseTypeCode")}
            value={context.media.licenseTypeCode}
            onChange={handlers.onAutocompleteChange("id", ["licenseTypeCode"])}
          />
          <AllowDownloadCheckbox
            value={context.media.allowDownload}
            onChange={handlers.onCheckboxChange("allowDownload")}
          />
        </Grid>

        {anyTrue([isAdmin, isIAPManager]) &&
          !isEmpty(context.media.stipProperties) && (
            <OrganizationAutoComplete
              freeSolo
              activeOnly
              label="Local Library"
              onChange={organizationName => {
                if (notBlank(organizationName)) {
                  // only send a single object with a single prop to API in order to update localLibrary
                  context.setMedia(
                    set(lensProp("stipProperties"), [
                      { localLibrary: organizationName },
                    ]),
                  );
                }
              }}
              valueProp="organizationName"
              value={getLocalLibrary(context.media.stipProperties)}
            />
          )}

        {includes(context.media.type, ["AUDIO", "VIDEO"]) && (
          <Grid item>
            <TextField
              required={isRequired("keywords")}
              label="Keywords"
              fullWidth
              value={context.media.keywords}
              onChange={handlers.onTextChange(["keywords"])}
              helperText="Keywords should be separated by commas"
            />
          </Grid>
        )}

        {context.media.type === "ARTICLE" && (
          <Grid item container spacing={2}>
            <Grid item xs={12} xl={4}>
              <TextField
                label="Volume (optional)"
                fullWidth
                inputProps={{
                  maxLength: 100,
                }}
                value={context.media.volume}
                onChange={handlers.onTextChange(["volume"])}
              />
            </Grid>

            <Grid item xs={12} xl={4}>
              <TextField
                label="Volume # (optional)"
                fullWidth
                inputProps={{
                  maxLength: 30,
                }}
                value={context.media.volumeNumber}
                onChange={handlers.onTextChange(["volumeNumber"])}
              />
            </Grid>

            <Grid item xs={12} xl={4}>
              <TextField
                label="Page Range (optional)"
                fullWidth
                inputProps={{
                  maxLength: 100,
                }}
                value={context.media.pageRange}
                onChange={handlers.onTextChange(["pageRange"])}
              />
            </Grid>
          </Grid>
        )}

        {includes(context.media.type, ["AUDIOBOOK", "BOOK"]) && (
          <Fragment>
            <Grid item container spacing={2}>
              <Grid item xs={12} xl={4}>
                <TextField
                  label="Volume (optional)"
                  fullWidth
                  inputProps={{
                    maxLength: 100,
                  }}
                  value={context.media.volume}
                  onChange={handlers.onTextChange(["volume"])}
                />
              </Grid>

              <Grid item xs={12} xl={4}>
                <TextField
                  label="Edition (optional)"
                  fullWidth
                  inputProps={{
                    maxLength: 100,
                  }}
                  value={context.media.edition}
                  onChange={handlers.onTextChange(["edition"])}
                />
              </Grid>
            </Grid>
          </Fragment>
        )}

        {context.media.type === "VIDEO" && (
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Series (optional)"
                fullWidth
                inputProps={{
                  maxLength: 255,
                }}
                value={context.media.series}
                onChange={handlers.onTextChange(["series"])}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Episode (optional)"
                fullWidth
                inputProps={{
                  maxLength: 255,
                }}
                value={context.media.episode}
                onChange={handlers.onTextChange(["episode"])}
              />
            </Grid>
          </Grid>
        )}

        <Grid item>
          <TextField
            label="Publisher (optional)"
            fullWidth
            inputProps={{
              maxLength: 255,
            }}
            value={context.media.publisherName}
            onChange={handlers.onTextChange(["publisherName"])}
          />
        </Grid>
      </Fragment>
    );
  };

  return (
    <EditColumn
      className={className}
      validator={validator}
      formBuilder={formBuilder}
    />
  );
};

export default EditContentInfoColumn;
